import { useState, useCallback } from 'react';
import saveAs from 'file-saver';
import {
  apiGetContentGenerationStatus,
  apiGetContentLibrary
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import {
  ContentLibraryRecord,
  MasterContentRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { parseAndMergeNewContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';
import { formatTime } from 'src/utils/dateformatting';
import { getQueryParamValue } from 'src/utils/app';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import {
  userMetadataServerProxy,
  GetUserProfilesResponse
} from 'src/serverProxy/userMetadataServerProxy';

/** Fetched content keyed by skuContentGenerationId (title, description, bullets) */
type FetchedContentMap = Map<string, { title: string; description: string; bullets: string[] }>;

export type ExportOptionId = 'exportAll' | 'exportGenerated' | 'exportCurrent' | 'exportUploaded';


interface ContentRow {
  retailerSku: string;
  /**
   * Matches Library Summary table labels: { current, uploaded, 1..n }
   */
  version: 'current' | 'uploaded' | `${number}`;
  /**
   * Status shown in the timeline (libraryStatus for versions / uploadedContent; "Current" for current content)
   */
  status: string;
  updatedBy: string;
  updatedAt: string;
  title: string;
  description: string;
  bullets: string[];
}

/**
 * Fetches all pages from the GetContentLibrary endpoint
 */
const fetchAllLibraryRecords = async (retailerId: number): Promise<ContentLibraryRecord[]> => {
  const allRecords: ContentLibraryRecord[] = [];
  const pageSize = 1000;
  let pageNumber = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await apiGetContentLibrary({
      retailerId,
      pageNumber,
      pageSize,
      filterNullLibraryStatus: true
    });

    const records = response.data.records;
    allRecords.push(...records);

    hasMore = records.length >= pageSize;
    pageNumber++;
  }

  return allRecords;
};

/**
 * Returns a date string for export from current/uploaded content (Updated At column).
 * Uses updatedAt when present; otherwise formats timestamp (upload time in ms) as MM/DD/YYYY.
 */
const getContentUpdatedAt = (content: MasterContentRecord): string => {
  if (content.updatedAt) return content.updatedAt;
  if (content.timestamp != null) return formatTime(content.timestamp, 'MM/DD/YYYY');
  return '';
};

const normalizeUpdatedBy = (value?: unknown): string => {
  if (typeof value !== 'string') return '';
  if (value === 'content-generation-workflow') return 'Stackline';
  if (value === 'SearchDataPublishPipeline') return 'Stackline';
  return value;
};

const normalizeLibraryStatusLabel = (value?: unknown, fallback: string = ''): string => {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  const normalized = value.trim();
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1).toLowerCase()}`;
};

const USER_METADATA_CHUNK_SIZE = 200;

const fetchUserMetadataByIds = async (userIds: string[]): Promise<GetUserProfilesResponse> => {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (ids.length === 0) return {};

  const out: GetUserProfilesResponse = {};
  for (let i = 0; i < ids.length; i += USER_METADATA_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + USER_METADATA_CHUNK_SIZE);
    const data = await userMetadataServerProxy.getUserProfiles(chunk);
    Object.assign(out, data ?? {});
  }
  return out;
};

const getUserDisplayName = (value: string, userMetadata: GetUserProfilesResponse): string => {
  if (!value) return '';
  if (value === 'Stackline') return 'Stackline';
  const meta = userMetadata[value];
  if (meta?.firstName && meta?.lastName) return `${meta.firstName} ${meta.lastName}`;
  if (meta?.email) return meta.email;
  return value;
};

interface VersionContentResult {
  title?: string;
  description?: string;
  bullets?: string[];
  version: ContentRow['version'];
  status: ContentRow['status'];
  updatedBy: ContentRow['updatedBy'];
  updatedAt: string;
  skuContentGenerationId: string;
}

const BATCH_SIZE = 50;

/**
 * Fetches title, description, bullets for the given skuContentGenerationIds via GetContentGenerationStatus.
 * Returns a map of id -> { title, description, bullets } (featureBullets mapped to bullets).
 */
const fetchContentForSkuIds = async (
  retailerId: number,
  beaconClientId: number,
  skuContentGenerationIds: string[]
): Promise<FetchedContentMap> => {
  const map: FetchedContentMap = new Map();
  if (skuContentGenerationIds.length === 0) return map;

  for (let i = 0; i < skuContentGenerationIds.length; i += BATCH_SIZE) {
    const batch = skuContentGenerationIds.slice(i, i + BATCH_SIZE);
    const response = await apiGetContentGenerationStatus({
      retailerId,
      beaconClientId,
      skuContentGenerationIds: batch
    });
    const record = response.data;
    if (!record) continue;
    for (const [id, item] of Object.entries(record)) {
      try {
        const parsed = parseAndMergeNewContent(item);
        map.set(id, {
          title: parsed.content?.title ?? '',
          description: parsed.content?.description ?? '',
          bullets: parsed.content?.featureBullets ?? []
        });
      } catch {
        // skip failed parse
      }
    }
  }
  return map;
};

/**
 * Collects skuContentGenerationIds that will be used in the export and don't have nested content.
 */
const getIdsNeedingContent = (
  records: ContentLibraryRecord[],
  exportType: ExportOptionId
): string[] => {
  const ids = new Set<string>();
  for (const record of records) {
    if (exportType === 'exportAll' || exportType === 'exportGenerated') {
      const generatedVersions = record.versions
        .filter((v) => v.type !== 'master')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      const latest = generatedVersions[0];
      if (latest && !latest.content) ids.add(latest.skuContentGenerationId);
    }
    if (exportType === 'exportAll') {
      const draftVersions = record.versions
        .filter(
          (v) => v.type !== 'master' && v.libraryStatus?.toLowerCase() === 'draft'
        )
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      const generatedVersions = record.versions
        .filter((v) => v.type !== 'master')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      const latestDraft = draftVersions[0];
      const latestGenerated = generatedVersions[0];
      const isDraftDifferent =
        latestDraft &&
        latestGenerated &&
        latestDraft.skuContentGenerationId !== latestGenerated.skuContentGenerationId;
      if (isDraftDifferent && latestDraft && !latestDraft.content) {
        ids.add(latestDraft.skuContentGenerationId);
      }
    }
  }
  return Array.from(ids);
};

/**
 * Extracts the latest generated version content and metadata from a library record.
 * When the API includes content on each version, that is used; else uses fetchedContentMap
 * when provided, then falls back to record title and empty description/bullets.
 */
const getLatestGeneratedContent = (
  record: ContentLibraryRecord,
  fetchedContentMap?: FetchedContentMap
): VersionContentResult | undefined => {
  const generatedVersions = record.versions
    .filter((v) => v.type !== 'master')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const latest = generatedVersions[0];
  if (!latest) return undefined;

  const content =
    latest.content ??
    fetchedContentMap?.get(latest.skuContentGenerationId) ?? {
      title: record.productName ?? '',
      description: '',
      bullets: [] as string[]
    };
  const versionLabel = String(generatedVersions.length) as ContentRow['version'];
  const status = normalizeLibraryStatusLabel(latest.libraryStatus, 'Unknown');
  const updatedBy = normalizeUpdatedBy(latest.updatedBy || latest.createdBy);
  return {
    ...content,
    version: versionLabel,
    status,
    updatedBy,
    updatedAt: latest.updatedAt,
    skuContentGenerationId: latest.skuContentGenerationId
  };
};

/**
 * Extracts the latest draft version (generated with libraryStatus 'draft') from a library record.
 * Uses nested content when present, else fetchedContentMap, then fallback to record title and empty description/bullets.
 */
const getLatestDraftContent = (
  record: ContentLibraryRecord,
  fetchedContentMap?: FetchedContentMap
): VersionContentResult | undefined => {
  const draftVersions = record.versions
    .filter(
      (v) =>
        v.type !== 'master' && v.libraryStatus?.toLowerCase() === 'draft'
    )
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const latest = draftVersions[0];
  if (!latest) return undefined;

  // To match the Library Summary numbering, we need the latest draft's index within ALL generated versions
  const allGeneratedVersions = record.versions
    .filter((v) => v.type !== 'master')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const idx = allGeneratedVersions.findIndex((v) => v.skuContentGenerationId === latest.skuContentGenerationId);
  const versionLabel = String(idx >= 0 ? allGeneratedVersions.length - idx : allGeneratedVersions.length) as ContentRow['version'];

  const content =
    latest.content ??
    fetchedContentMap?.get(latest.skuContentGenerationId) ?? {
      title: record.productName ?? '',
      description: '',
      bullets: [] as string[]
    };
  const status = normalizeLibraryStatusLabel(latest.libraryStatus, 'Unknown');
  const updatedBy = normalizeUpdatedBy(latest.updatedBy || latest.createdBy);
  return {
    ...content,
    version: versionLabel,
    status,
    updatedBy,
    updatedAt: latest.updatedAt,
    skuContentGenerationId: latest.skuContentGenerationId
  };
};

/**
 * Converts a MasterContentRecord or version content into a ContentRow
 */
const toContentRow = (
  record: ContentLibraryRecord,
  row: Pick<ContentRow, 'version' | 'status' | 'updatedBy' | 'updatedAt'>,
  content: { title?: string; description?: string; bullets?: string[] },
): ContentRow => ({
  retailerSku: record.retailerSku,
  version: row.version,
  status: row.status,
  updatedBy: row.updatedBy,
  updatedAt: row.updatedAt,
  title: content.title || '',
  description: content.description || '',
  bullets: content.bullets || []
});

/**
 * Transforms library records into flat rows based on export type.
 * fetchedContentMap is used to fill title/description/bullets for versions that don't have nested content.
 */
const transformRecordsToRows = (
  records: ContentLibraryRecord[],
  exportType: ExportOptionId,
  fetchedContentMap?: FetchedContentMap
): ContentRow[] => {
  const rows: ContentRow[] = [];

  for (const record of records) {
    if (exportType === 'exportAll' || exportType === 'exportCurrent') {
      if (record.currentContent) {
        const updatedBy = normalizeUpdatedBy(record.currentContent.debug?.updatedBy);
        rows.push(
          toContentRow(
            record,
            {
              version: 'current',
              status: 'Current',
              updatedBy,
              updatedAt: getContentUpdatedAt(record.currentContent)
            },
            record.currentContent
          )
        );
      }
    }

    const generatedContent =
      exportType === 'exportAll' || exportType === 'exportGenerated'
        ? getLatestGeneratedContent(record, fetchedContentMap)
        : null;
    if (generatedContent) {
      if (exportType === 'exportAll' || exportType === 'exportGenerated') {
        rows.push(
          toContentRow(
            record,
            {
              version: generatedContent.version,
              status: generatedContent.status,
              updatedBy: generatedContent.updatedBy,
              updatedAt: generatedContent.updatedAt
            },
            generatedContent
          )
        );
      }
    }

    if (exportType === 'exportAll' || exportType === 'exportUploaded') {
      const uploadedOrMasterContent = record.uploadedContent ?? record.masterContent;
      if (uploadedOrMasterContent) {
        const updatedBy = normalizeUpdatedBy(uploadedOrMasterContent.debug?.updatedBy);
        rows.push(
          toContentRow(
            record,
            {
              version: 'uploaded',
              status: normalizeLibraryStatusLabel(uploadedOrMasterContent.libraryStatus, 'Unknown'),
              updatedBy,
              updatedAt: getContentUpdatedAt(uploadedOrMasterContent)
            },
            uploadedOrMasterContent
          )
        );
      }
    }

    const draftContent = getLatestDraftContent(record, fetchedContentMap);
    const isDraftSameAsGenerated =
      draftContent &&
      generatedContent &&
      draftContent.skuContentGenerationId === generatedContent.skuContentGenerationId;

    if (exportType === 'exportAll' && draftContent && !isDraftSameAsGenerated) {
      rows.push(
        toContentRow(
          record,
          {
            version: draftContent.version,
            status: draftContent.status,
            updatedBy: draftContent.updatedBy,
            updatedAt: draftContent.updatedAt
          },
          draftContent
        )
      );
    }
  }

  return rows;
};

/**
 * Escapes a CSV cell value (handles double quotes and commas)
 */
const escapeCSV = (value: string): string => {
  return `"${value.replace(/"/g, '""')}"`;
};

/**
 * Converts content rows to a CSV string and triggers a browser download
 */
const downloadCSV = (rows: ContentRow[]) => {
  // Determine max bullet count across all rows
  let maxBullets = 0;
  for (const row of rows) {
    maxBullets = Math.max(maxBullets, row.bullets.length);
  }

  // Build headers
  const headers = ['Retailer SKU', 'Status', 'Title'];
  for (let i = 0; i < maxBullets; i++) {
    headers.push(`Bullet ${i + 1}`);
  }
  headers.push('Description');
  // Put metadata columns at the end (per UI table shape)
  headers.push('Version', 'Updated By', 'Updated At');

  // Build CSV rows
  const csvRows = rows.map((row) => {
    const cells = [
      escapeCSV(row.retailerSku),
      escapeCSV(row.status),
      escapeCSV(row.title)
    ];

    for (let i = 0; i < maxBullets; i++) {
      cells.push(escapeCSV(row.bullets[i] || ''));
    }

    cells.push(escapeCSV(row.description));
    cells.push(
      escapeCSV(row.version),
      escapeCSV(row.updatedBy),
      escapeCSV(
        row.updatedAt
          ? row.updatedAt.includes('T')
            ? formatTime(row.updatedAt, 'MM/DD/YYYY')
            : row.updatedAt
          : ''
      )
    );
    return cells.join(',');
  });

  const csvContent = [headers.join(','), ...csvRows].join('\n');

  // UTF-8 BOM for Excel compatibility
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `content_library_${new Date().toISOString().slice(0, 10)}.csv`);
};

/**
 * Hook for exporting content library data to CSV.
 * Fetches all pages from the GetContentLibrary endpoint and converts to CSV
 * based on the selected export type.
 */
export const useLibraryExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const beaconClientId = useBeaconClientId();

  const handleExport = useCallback(
    async (exportType: ExportOptionId) => {
      const retailerId = parseInt(getQueryParamValue('rid') || '1');

      setIsExporting(true);
      try {
        const allRecords = await fetchAllLibraryRecords(retailerId);

        const idsNeedingContent = getIdsNeedingContent(allRecords, exportType);
        const fetchedContentMap =
          idsNeedingContent.length > 0 && typeof beaconClientId === 'number' && beaconClientId > 0
            ? await fetchContentForSkuIds(retailerId, beaconClientId, idsNeedingContent)
            : undefined;

        const rows = transformRecordsToRows(allRecords, exportType, fetchedContentMap);

        if (rows.length === 0) {
          throw new Error('No content found to export.');
        }

        const updatedByUserIds = rows
          .map((r) => r.updatedBy)
          .filter((v): v is string => Boolean(v))
          .filter((v) => v !== 'Stackline');
        const userMetadata = await fetchUserMetadataByIds(updatedByUserIds);
        const rowsWithDisplayNames = rows.map((row) => ({
          ...row,
          updatedBy: getUserDisplayName(row.updatedBy, userMetadata)
        }));

        downloadCSV(rowsWithDisplayNames);
        return { success: true };
      } catch (error) {
        console.error('Library export error:', error);
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    [beaconClientId]
  );

  return { handleExport, isExporting };
};
